/**
 * `<CardRow>` / `<CardList>` — the canonical card-with-verdict-rail layout.
 *
 * Segment rendering mirrors jackhammer-ui's PDF report
 * (`reportRenderer/components/CardBody.jsx`):
 *   - highlighted segments get the user's `highlightColor`, bold + underline
 *   - non-highlighted segments shrink to ~8px
 *   - non-highlighted segments past `OVERFLOW_THRESHOLD` chars shrink to 3px
 *
 * Sized for screen (12 / 8 / 3 px), not print (8 / 6 / 3 pt).
 *
 * Below 1000px the verdict rail merges inline: the rating pill moves into the
 * card header (so the verdict is still flagged at the top), and the reason
 * text moves into a bordered block at the bottom of the card.
 */

import type { Card } from '@eloquor/api-contract';
import { RatingPill } from '../ratings/RatingPill';
import { useMediaQuery } from '../../hooks/useMediaQuery';

const OVERFLOW_THRESHOLD = 3000;

interface CardListProps {
  cards: Card[];
  highlightColor: string;
}

export function CardList({ cards, highlightColor }: CardListProps) {
  const isNarrow = useMediaQuery('(max-width: 999px)');
  return (
    <div
      style={{
        marginTop: 24,
        display: 'grid',
        gridTemplateColumns: isNarrow ? '1fr' : 'minmax(0, 1fr) 280px',
        columnGap: 24,
        rowGap: 16,
      }}
    >
      {cards.map((c) => (
        <CardRow key={c.id} card={c} highlightColor={highlightColor} />
      ))}
    </div>
  );
}

interface CardRowProps {
  card: Card;
  highlightColor: string;
}

export function CardRow({ card, highlightColor }: CardRowProps) {
  const isNarrow = useMediaQuery('(max-width: 999px)');
  const isUnchecked = card.verdict === 'unchecked';
  const reasonText = isUnchecked ? 'Not yet checked.' : card.reason;
  const errorRef = card.verdict === 'error' && card.errorId ? card.errorId : null;

  const articleStyle = {
    background: 'var(--vellum)',
    border: '1px solid var(--border)',
    borderLeft: `3px solid var(--rt-${card.verdict})`,
    borderRadius: 8,
    padding: '16px 20px',
  };

  const cardBody = (
    <>
      <header style={{ display: 'flex', alignItems: 'baseline', gap: 10, marginBottom: 6 }}>
        <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--fg-3)' }}>
          #{String(card.ord).padStart(3, '0')}
        </span>
        <strong style={{ fontFamily: 'var(--font-doc)', fontWeight: 700, fontSize: 15, flex: 1, minWidth: 0 }}>
          {card.tag}
        </strong>
        {isNarrow && <RatingPill verdict={card.verdict} size="sm" />}
      </header>
      <div style={{ fontFamily: 'var(--font-doc)', fontSize: 13, color: 'var(--fg-3)', marginBottom: 8 }}>
        {card.cite}
      </div>
      <div style={{ fontFamily: 'var(--font-doc)', lineHeight: 1.2, fontSize: 0 }}>
        {card.segments.map((s, i) => {
          if (s.highlighted) {
            return (
              <span
                key={i}
                style={{
                  fontSize: "11pt",
                  fontWeight: 700,
                  textDecoration: 'underline',
                  background: highlightColor,
                  padding: '0 1px',
                }}
              >
                {s.text}
              </span>
            );
          }
          const fontSize = s.text.length < OVERFLOW_THRESHOLD ? "7pt" : "4pt";
          return (
            <span key={i} style={{ fontSize, lineHeight: fontSize, color: 'var(--fg-3)' }}>
              {s.text}
            </span>
          );
        })}
      </div>
    </>
  );

  if (isNarrow) {
    return (
      <article style={articleStyle}>
        {cardBody}
        <div
          style={{
            marginTop: 12,
            paddingTop: 10,
            borderTop: isUnchecked ? '1px dashed var(--border)' : '1px solid var(--border)',
          }}
        >
          <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 }}>{reasonText}</p>
          {errorRef && <ErrorRef id={errorRef} />}
        </div>
      </article>
    );
  }

  return (
    <>
      <article style={articleStyle}>{cardBody}</article>
      <aside
        style={{
          background: isUnchecked ? 'transparent' : 'var(--vellum)',
          border: isUnchecked ? '1px dashed var(--border)' : '1px solid var(--border)',
          borderRadius: 8,
          padding: '14px 16px',
          alignSelf: 'start',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
          <RatingPill verdict={card.verdict} size="sm" />
          <span style={{ fontFamily: 'var(--font-mono)', fontSize: 10.5, color: 'var(--fg-3)' }}>
            on #{String(card.ord).padStart(3, '0')}
          </span>
        </div>
        <p style={{ margin: 0, fontSize: 13, color: 'var(--fg-2)', lineHeight: 1.5 }}>{reasonText}</p>
        {errorRef && <ErrorRef id={errorRef} />}
      </aside>
    </>
  );
}

function ErrorRef({ id }: { id: string }) {
  return (
    <p
      style={{
        margin: '6px 0 0',
        fontFamily: 'var(--font-mono)',
        fontSize: 11,
        color: 'var(--fg-3)',
        userSelect: 'all',
      }}
      title="Include this ID when reporting the error."
    >
      Error ref: {id}
    </p>
  );
}
